import { Injectable } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {
  FieldGroupMetaData,
  FieldMetaData,
  FieldsData,
  SelectFieldOption
} from '@models/checkout.model';
import { Notification } from '@models/notifications.model';
import { FieldItem, ShoppingCartData, SourceOrderDetails } from '@models/shopping-cart.model';
import { FieldErrorMessage, ValidateFields } from '@models/shopping.model';
import { TranslationService } from '@services/translation/translation.service';
import { VALIDATOR_INPUTS } from '@utils/custom-form.utils';
import { ExpiryDateValidatorUtils } from '@utils/expiry-date-validator.utils';

@Injectable({
  providedIn: 'root'
})
export class CustomFormService {
  constructor(private form_builder: FormBuilder, private translation_svc: TranslationService) {}

  createForm(json_form_data: FieldsData['groups']): FormGroup {
    return this.form_builder.group({
      groups: this.form_builder.array(
        json_form_data.map(
          (group: FieldGroupMetaData) =>
            this.form_builder.group({
              id: [group.id],
              name: [group.name],
              display_name: [group.display_name],
              fields: this.createGroupField(group.fields) as FormGroup
            }) as FormGroup
        )
      ) as FormArray
    }) as FormGroup;
  }

  parseFieldValue(json_form_data: FieldsData['groups'], extras?: SourceOrderDetails): FieldItem[] {
    const fields_value: FieldItem[] = [];
    json_form_data.forEach((group: FieldGroupMetaData) => {
      group.fields.forEach((field: FieldMetaData) => {
        if (field.control.value !== null) {
          if (group.fields.filter((f: FieldMetaData) => f.id == field.id).length > 1) {
            const obj: { [k: string]: string } = {};
            const _value: FieldItem['value'] = fields_value.find((f: FieldItem) => f.id == field.id)
              ?.value as { [key: string]: string };

            if (_value) {
              _value[field.name] = field.control.value;
            } else {
              obj[field.name] = field.control.value;
              fields_value.push({
                id: field.id,
                value: obj,
                additional_info: field.additional_info
              });
            }

            // Should include name when field name is country_id
            if (field.additional_info?.activity_field && field.name === 'country_id')
              obj['iso3166_country'] = field.options?.find(
                (val: SelectFieldOption) => val.value === field.control.value
              )?.label!;
          } else {
            fields_value.push({
              id: field.id,
              value: this.getPayloadValue(field, extras),
              additional_info: field.additional_info
            });
          }
        }
      });
    });
    return fields_value;
  }

  buildFormData(body: ShoppingCartData): FormData {
    const formData: FormData = new FormData();
    formData.append(
      'body',
      new Blob([JSON.stringify(body)], {
        type: 'application/json'
      })
    );
    return formData;
  }

  validationNotificationsBanner(response: ValidateFields): Notification[] {
    const errors: string = response.error_messages
      ? ` <span class="d-block fw-light mt-3"> ${this.translation_svc.translate(
          'validation_error_statement',
          response.error_messages.length.toString()
        )}: </span>
          <ul>${response.error_messages
            ?.map((item: FieldErrorMessage) =>
              item.error_message
                .map((message: string) => `<li class="fw-light">${message}</li>`)
                .join('')
            )
            .join('')}</ul>`
      : '';

    return [
      {
        subject: this.translation_svc.translate('validation_error_title'),
        notification: errors
      }
    ];
  }

  setFormErrors(
    json_form_data: FieldGroupMetaData[],
    response: ValidateFields
  ): FieldGroupMetaData[] {
    response.error_messages?.forEach((item: FieldErrorMessage) => {
      json_form_data.forEach((group: FieldGroupMetaData) => {
        group.fields.forEach((field: FieldMetaData) => {
          if (
            VALIDATOR_INPUTS.includes(field.type) &&
            field.id.toString() == item.id &&
            (new Array(field.source_id, field.output_path).includes(item.field_id) ||
              (field.additional_info?.custom_field && field.name === item.field_id))
          ) {
            field.control.markAsDirty();
            field.control?.setErrors({
              _error: item.error_message
            });
          }
        });
      });
    });
    return json_form_data;
  }

  private getPayloadValue(field: FieldMetaData, extras?: SourceOrderDetails): FieldItem['value'] {
    if (extras && extras.comments && field.name === 'comment')
      return `${extras.comments}\n\n${field.control.value ?? ''}`;

    return field.control.value;
  }

  private createGroupField(fields: FieldGroupMetaData['fields']): FormGroup {
    const fields_group: { [k: string]: FormControl } = {};
    fields.forEach((field: FieldMetaData) => {
      let controlValidators: ValidatorFn[] = [];

      this.setupInitialValue(field);

      if (VALIDATOR_INPUTS.includes(field.type) && field.required)
        controlValidators.push(Validators.required);

      if (field.type === 'expiry_date')
        controlValidators.push(ExpiryDateValidatorUtils.expiryDateValidator);

      fields_group[`${field.id}_${field.name}`] = new FormControl(
        { value: field.value ?? null, disabled: field.disabled },
        { validators: controlValidators, updateOn: this.fieldUpdateOn(field) }
      );
      field.control = fields_group[`${field.id}_${field.name}`] as FormControl;

      if (field.type === 'expiry_date' && field.value && field.control.invalid)
        field.control.markAsDirty();
    });
    return new FormGroup(fields_group);
  }

  private fieldUpdateOn(field: FieldMetaData): 'change' | 'blur' | 'submit' | undefined {
    if (['text', 'date', 'expiry_date'].includes(field.type) && !field.on_change) return 'blur';
    return 'change';
  }

  private setupInitialValue(field: FieldMetaData): void {
    switch (field.type) {
      case 'boolean':
      case 'checkbox':
        field.value = ['true', 'yes'].includes(`${field.value}`.toLowerCase()) ? true : false;
        break;

      case 'date':
        field.value =
          field.value == '' || !!isNaN(Date.parse(`${field.value}`))
            ? null
            : `${field.value}`.replace(/(^|-)0+/g, '$1');
        break;

      case 'select':
        field.value = field.value ?? '';
        break;

      case 'hidden':
        field.disabled = true;
        break;

      default:
        break;
    }
  }
}
